import { AssetSubClassCode } from '@api/api/entity-constants';
import { Entity } from '@api/api/entity-schema';
import { RangeHorizon, rangeToResampleMapping } from '@chart';
import { Card } from '@design/components/card/Card';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { DailyUpdateCard } from '@terminal/components/daily-update-card/DailyUpdateCard';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { ErrorMessageWrapper } from '@terminal/components/error-message-wrapper/ErrorMessageWrapper';
import { MonthlySentimentCards } from '@terminal/components/monthly-sentiment/MonthlySentimentCards';
import { isEconomicSeries } from '@terminal/components/price-display-view/helpers/price-display-helpers';
import { useChartNavigate } from '@terminal/hooks/use-chart-navigate/useChartNavigate';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { ChartLoading } from '@terminal/modules/simple-chart/components/chart-loading/ChartLoading';
import { SimpleChart } from '@terminal/modules/simple-chart/SimpleChart';
import { useFilteredInsights } from '@terminal/react-query-hooks/insights/useFilteredInsights';
import { isLongTail } from '@terminal/react-query-hooks/long-tail-series/useLongTailSeries';
import { queryKeys } from '@terminal/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useLanguageStore } from '@terminal/stores/use-language-store/useLanguageStore';
import { ChartSearchParamsAssetValue } from '@terminal/utils/chart-search-utils/chart-search-utils';
import { SentimentBreakdownCard } from '@terminal/views/theme-overview/tab-content/pulse-overview/components/sentiment-breakdown/SentimentBreakdownCard';
import { MonthlySentimentDrawer } from '@terminal/views/theme-overview/tab-content/pulse-overview/drawer/MonthlySentimentDrawer';
import { useEntityPulseSentimentBreakdown } from '@terminal/views/theme-overview/tab-content/pulse-overview/hooks/usePulseSentimentBreakdown';
import {
  usePulseDailyUpdate,
  usePulseMonthlySummaries,
} from '@terminal/views/theme-overview/tab-content/pulse-overview/hooks/usePulseSummaries';
import { PulseSentimentDirection } from '@terminal/views/theme-overview/tab-content/pulse-overview/pulse-types';
import { MENTIONS_WINDOW_DAYS } from '@terminal/views/theme-overview/tab-content/pulse-overview/services/theme-mentions-service';
import { ChartState } from '@terminal/views/turbo-chart/hooks/use-chart-state/useChartState';
import { ChartSettings } from '@terminal/views/turbo-chart/utils/chart-settings-utils/chart-settings-utils';
import clsx from 'clsx';
import { endOfDay, format, type Locale, startOfDay, subDays } from 'date-fns';
import { enUS, ja, ko } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { EarningsSection } from './components/earnings-section/EarningsSection';
import styles from './Overview.module.scss';

export interface OverviewProps {
  entity: Entity;
  isSeriesLoading?: boolean;
  initialHorizon?: RangeHorizon;
}

export const OverviewChart = ({
  entity,
  isSeriesLoading = false,
  initialHorizon,
}: OverviewProps) => {
  const chartNavigate = useChartNavigate();
  const horizon =
    initialHorizon ??
    (isEconomicSeries(entity) ? RangeHorizon.OneYear : RangeHorizon.OneDay);
  const chartSettings: ChartSettings = {
    assets: [
      {
        tag: entity.tag,
        snake: entity.default_snake,
        paneIndex: 0,
      },
    ],
    resample: rangeToResampleMapping[horizon],
    horizon,
    domain: null,
    hidden: undefined,
    paneSettings: {},
  };

  const navigateToChart = (chartState: ChartState) => {
    const entityValue: ChartSearchParamsAssetValue = [
      entity.tag,
      entity.default_snake,
      0,
    ];

    chartNavigate({
      assets: [entityValue],
      configParams: {
        resample: chartState.resample,
        horizon: chartState.horizon,
        seriesType: chartState.chartPanes[0]?.seriesType,
      },
    });
  };

  return isSeriesLoading ? (
    <ChartLoading isLegacyVariant={false} isChatMessage={false} />
  ) : (
    (!isLongTail(entity) || !!entity.default_snake.trim()) && (
      <SimpleChart
        entity={entity}
        key={entity.tag}
        initialChartSettings={chartSettings}
        isLegacyVariant={false}
        hasLineColourBasedOnPrice={!isEconomicSeries(entity)}
        onClickFullscreen={navigateToChart}
        maxHorizon={RangeHorizon.TenYears}
        eventsConfig={{
          TMI: true,
        }}
        source={POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW}
      />
    )
  );
};

const DATE_LOCALES: Partial<Record<string, Locale>> = { en: enUS, ja, ko };

export const Overview = ({ entity, isSeriesLoading }: OverviewProps) => {
  const { t } = useTranslation(['widget']);
  const { setQueryParams } = useQueryParams();
  const languageSetting = useLanguageStore(state => state.languageSetting);
  const { trackMonthlySentimentDrawerOpen } = usePosthogEvents();
  const dailyUpdate = usePulseDailyUpdate({
    id: entity.tag,
    subjectType: 'entity',
  });
  const monthlySummaries = usePulseMonthlySummaries({
    id: entity.tag,
    subjectType: 'entity',
  });
  const { data: insights, isLoading: isInsightsLoading } = useFilteredInsights(
    {
      entity: [entity.tag],
      from_date: startOfDay(subDays(new Date(), MENTIONS_WINDOW_DAYS)),
      to_date: endOfDay(new Date()),
    },
    100
  );
  const sentimentBreakdown = useEntityPulseSentimentBreakdown({
    id: entity.tag,
    subjectType: 'entity',
    scenarioInsights: insights?.data?.result ?? undefined,
  });

  const openSentimentDrawer = (direction: PulseSentimentDirection) => {
    trackMonthlySentimentDrawerOpen(
      POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW,
      direction
    );
    setQueryParams(
      { [queryKeys.monthlySentiment]: direction },
      { replace: true }
    );
  };

  const isStock = entity.sub_class === AssetSubClassCode.EquityStock;

  return (
    <div className={styles.overviewRoot}>
      <MonthlySentimentDrawer
        subject={{ type: 'entity', item: entity }}
        source={POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW}
      />
      <OverviewChart entity={entity} isSeriesLoading={isSeriesLoading} />
      <div className={styles.grid}>
        <div
          className={styles.gridSection}
          data-testid="asset-overview-daily-update"
        >
          <div className={styles.row}>
            <h3 className={styles.cardTitle}>
              {t('widget:assetOverview.sentiment.recentUpdates')}
            </h3>
            {dailyUpdate.data?.createdAt && (
              <span className={styles.recentUpdatesMeta}>
                {t('widget:themeOverview.pulse.lastUpdatedAt', {
                  date: format(
                    new Date(dailyUpdate.data?.createdAt),
                    'h:mm a, MMM d',
                    {
                      locale: DATE_LOCALES[languageSetting],
                    }
                  ),
                })}
              </span>
            )}
          </div>
          <Card className={styles.card}>
            {dailyUpdate.isLoading ? (
              <SkeletonLoader
                areas={[
                  {
                    styles: {
                      height: '100px',
                      width: '100%',
                      borderRadius: '8px',
                    },
                  },
                ]}
              />
            ) : dailyUpdate.data?.content ? (
              <DailyUpdateCard
                update={dailyUpdate.data.content}
                source={POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW}
              />
            ) : (
              <ErrorMessageWrapper
                variant="empty"
                showBorder={false}
                className={styles.emptyErrorWrapper}
                title={t(
                  'widget:assetOverview.sentiment.recentUpdatesEmptyTitle'
                )}
                subTitle={t(
                  'widget:assetOverview.sentiment.recentUpdatesEmptySubTitle'
                )}
              />
            )}
          </Card>
        </div>
        {isStock && (
          <div
            className={styles.gridSection}
            data-testid="asset-overview-earnings"
          >
            <ErrorBoundaryWrapper resetKeys={[entity.tag]}>
              <EarningsSection entity={entity} />
            </ErrorBoundaryWrapper>
          </div>
        )}
        <div
          className={clsx(styles.gridSection, !isStock && styles.nextRow)}
          data-testid="asset-overview-monthly-sentiment"
        >
          <h3 className={styles.cardTitle}>
            {t('widget:assetOverview.sentiment.monthlySentimentSummary')}
          </h3>
          {monthlySummaries.isLoading ? (
            <SkeletonLoader
              className={styles.card}
              areas={[
                {
                  styles: {
                    height: '100%',
                    width: '100%',
                    borderRadius: '8px',
                  },
                },
              ]}
            />
          ) : (
            <MonthlySentimentCards
              summaries={monthlySummaries.data}
              subject={{ type: 'entity', item: entity }}
              source={POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW}
              getCardTitle={direction =>
                t(
                  `widget:assetOverview.sentiment.directionSentiment_${direction}`
                )
              }
              onOpenDrawer={openSentimentDrawer}
            />
          )}
        </div>
        <div
          className={styles.gridSection}
          data-testid="asset-overview-sentiment-breakdown"
        >
          <h3 className={styles.cardTitle}>
            {t('widget:assetOverview.sentiment.sentimentBreakdown')}
          </h3>
          <Card className={styles.card}>
            {sentimentBreakdown.isLoading || isInsightsLoading ? (
              <SkeletonLoader
                className={styles.card}
                areas={[
                  {
                    styles: {
                      height: '100%',
                      width: '100%',
                      borderRadius: '8px',
                    },
                  },
                ]}
              />
            ) : sentimentBreakdown.data &&
              Object.values(sentimentBreakdown.data.totals).some(
                count => count > 0
              ) ? (
              <SentimentBreakdownCard
                breakdown={sentimentBreakdown.data}
                onOpenDrawer={openSentimentDrawer}
              />
            ) : (
              <ErrorMessageWrapper
                variant="empty"
                displayIcon
                showBorder={false}
              />
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};
